import {AddButton, intlShape, utils, ClickItem} from '@wallaby-ui/core';
import classNames from 'classnames';
import {isArray, isNil, pick} from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import {defineMessages, injectIntl} from 'react-intl';

import styles from './ArrayComponent.less';
import ArrayItem from './ArrayItem';
import {I18N as I18NTextBox} from './TextBox';
import stylesAutoLayout from '../AutoLayout.less';
import {LIBRARY_NAME} from 'src/js/common/constants';
import ExpandDataButton from 'src/js/containers/auto_layout/components/ExpandDataButton';
import ModifiedAsterisk from 'src/js/containers/auto_layout/components/ModifiedAsterisk';
import LayoutRenderer from 'src/js/lib/auto_layout/auto_layout_renderer';
import AutoLayoutService from 'src/js/lib/auto_layout/auto_layout_service';
import {
  ContentComponent,
  ContentProperty,
} from 'src/js/model/auto_layout_content_schema';
import {
  LayoutComponent,
  LayoutProperty,
} from 'src/js/model/auto_layout_layout_schema';
import {MetadataSchema} from 'src/js/model/auto_layout_metadata_schema';

const I18N = defineMessages({
  ADD_BUTTON: {
    id: 'autoLayout.array.button.add',
    defaultMessage: 'Add array item',
    description: 'text for auto layout array add button',
  },
});

const logger = utils.LogUtils.getLogger('ArrayComponent', LIBRARY_NAME);

class ArrayComponent extends React.PureComponent {
  constructor() {
    super();

    this.state = {
      expandedData: null,
    };

    this.add = this.add.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onRemoveItem = this.onRemoveItem.bind(this);
    this.handleDataExpand = this.handleDataExpand.bind(this);
  }

  handleChange(newData) {
    const {onDataChange} = this.props;
    onDataChange(this.getOnDataChangeData(newData));
  }

  handleDataExpand() {
    const {layout} = this.props;
    const {expandedData} = this.state;
    if (layout.hasExpandableData()) {
      this.setState({expandedData: !expandedData});
    }
  }

  onRemoveItem(dlOnRemoveData) {
    const {data} = this.props;
    if (this.isReadOnly()) {
      throw Error('Cannot remove item from readOnly array."');
    } else {
      this.removeFromArray(data, dlOnRemoveData.itemIdx);
    }
  }

  getOnDataChangeData(newData) {
    const {layout} = this.props;
    return {
      ...pick(this.props, ['pathToData', 'data']),
      label: layout.getLabel(),
      newData,
    };
  }

  add(_item?: ClickItem): void {
    const {data} = this.props;
    if (!this.isReadOnly()) {
      // add new null object in array, empty object is created by layout_renderer
      const newData = [null, ...data];

      this.handleChange(newData);
    }
  }

  /**
   * This method call is generated in layout_renderer.
   * @param itemsArray
   * @param idx
   */
  removeFromArray(itemsArray, idx) {
    if (!this.isReadOnly()) {
      const newData = utils.stateUtils.removeItemFromArray(itemsArray, idx);

      this.handleChange(newData);
    }
  }

  isReadOnly() {
    const {layout, readOnly} = this.props;
    return layout.getReadOnly() || readOnly === true;
  }

  render() {
    const {
      arrayContent,
      arrayLayout,
      data,
      dataChanges,
      idSuffix,
      intl,
      isCollapsed,
      layout,
      noLabel,
      pathToData,
      screenId,
      textOnly,
    } = this.props;
    const {expandedData} = this.state;
    const inputName = [pathToData, 'input'].join('_');
    const isReadOnly = this.isReadOnly();
    const isTextOnly = textOnly === true;
    const isEmpty = !data || (isArray(data) && data.length === 0);
    const isEmptyAndTextOnly = isTextOnly && isEmpty;
    const hideLabel = noLabel === true;
    const label = layout.getLabel()
      ? utils.TranslateUtils.translate({id: layout.getLabel()}, intl)
      : undefined;
    const isReallyCollapsed = !!layout.isCollapsible() && isCollapsed;
    // if component hasExpandableData is true, isExpandedData is false until first expand
    // when expandedData in state is initialized
    const isDataExpanded = isNil(expandedData)
      ? !layout.hasExpandableData()
      : expandedData;
    const layoutItemKeys = arrayLayout
      .getLayoutItems()
      .getSortedPropertyNames();
    const isChanged = dataChanges && !isNil(dataChanges[pathToData]);
    if (!isEmpty && !isArray(data)) {
      logger.error(`Screen data for path: ${pathToData} should be an array.`);
    }
    return (
      <div
        id={AutoLayoutService.getComponentId(screenId, pathToData, idSuffix)}
        className={classNames(
          styles.dlArrayComponent,
          stylesAutoLayout.dlScreenComponent,
          {
            [stylesAutoLayout.dlTextOnly]: isTextOnly,
          }
        )}
        hidden={isReallyCollapsed}
      >
        {/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/label-has-for */}
        <label
          htmlFor={inputName}
          title={label}
          className={stylesAutoLayout.dlArrayLabel}
          hidden={hideLabel}
        >
          <ModifiedAsterisk isModified={isChanged} />
          {label}
        </label>
        {/* eslint-enable jsx-a11y/label-has-associated-control, jsx-a11y/label-has-for */}
        <div
          className={classNames(
            stylesAutoLayout.dlScreenComponentInput,
            styles.dlArrayItems,
            {
              [styles.dlArrayItemsWithExpandButton]: layout.hasExpandableData(),
              [styles.dlArrayItemsWithoutExpandButton]:
                !layout.hasExpandableData(),
            }
          )}
          hidden={isEmpty}
        >
          {data &&
            data.length > 0 &&
            data.map &&
            data.map((dataItem, dataIdx) => {
              // if data are not expanded, render only first item
              if (isDataExpanded || dataIdx === 0) {
                // render in reverse order
                const index = data.length - 1 - dataIdx;
                const isLast = dataIdx === data.length - 1;
                const ArrayItemComponent = React.createElement(
                  ArrayItem,
                  LayoutRenderer.getArrayItemProps(
                    layoutItemKeys,
                    {
                      ...this.props,
                      content: arrayContent,
                      layout: arrayLayout,
                      // key: index,
                      onRemove: this.onRemoveItem,
                      readOnly: isReadOnly,
                      textOnly: isTextOnly,
                      isLast,
                    },
                    index
                  ),
                  []
                );
                return (
                  <div
                    className={classNames({
                      [styles.dlArrayItemComponentWrapper]:
                        layout.hasExpandableData(),
                    })}
                    key={index}
                  >
                    {ArrayItemComponent}
                    {layout.getSeparator() && isDataExpanded && !isLast && (
                      <hr className={styles.dlLineSeparator} />
                    )}
                  </div>
                );
              }
              return null;
            })}
          {layout.hasExpandableData() && data.length > 1 ? (
            <div className={styles.dlArrayItemsCollapseButton}>
              <ExpandDataButton
                expandCallback={this.handleDataExpand}
                className={classNames(styles.dlArrayExpandDataButton)}
                isExpanded={isDataExpanded}
                dataLength={data.length}
              />
            </div>
          ) : null}
        </div>
        <div
          className={classNames(styles.dlArrayAddButtonWrapper, {
            [stylesAutoLayout.dlNoLabel]: hideLabel,
          })}
        >
          {isReadOnly ? null : (
            <AddButton
              addCallback={this.add}
              className={classNames(styles.dlArrayAddButton, {
                'float-end': hideLabel,
              })}
              disabled={isReadOnly}
              titleText={I18N.ADD_BUTTON.id}
            />
          )}
        </div>
        {isEmptyAndTextOnly ? (
          <span
            className={stylesAutoLayout.dlScreenComponentInput}
            title={utils.TranslateUtils.translate(I18NTextBox.EMPTY_DATA, intl)}
          >
            {utils.TranslateUtils.translate(I18NTextBox.EMPTY_DATA, intl)}
          </span>
        ) : null}
      </div>
    );
  }
}

ArrayComponent.displayName = 'ArrayComponent';

/* eslint-disable react/no-unused-prop-types */
ArrayComponent.propTypes = {
  autoLayoutStateId: PropTypes.string.isRequired,
  content: PropTypes.instanceOf(ContentProperty).isRequired,
  dataChanges: PropTypes.objectOf(PropTypes.any),
  dataSets: PropTypes.objectOf(PropTypes.any).isRequired,
  layout: PropTypes.instanceOf(LayoutProperty).isRequired,
  rootData: PropTypes.objectOf(PropTypes.any).isRequired,
  metadata: PropTypes.instanceOf(MetadataSchema).isRequired,
  screenId: PropTypes.string.isRequired,
  idSuffix: PropTypes.string,
  isCollapsed: PropTypes.bool,
  intl: intlShape.isRequired,
  data: PropTypes.arrayOf(PropTypes.any),
  pathToData: PropTypes.string,
  arrayContent: PropTypes.instanceOf(ContentComponent).isRequired,
  arrayLayout: PropTypes.instanceOf(LayoutComponent).isRequired,
  readOnly: PropTypes.bool,
  textOnly: PropTypes.bool,
  expanded: PropTypes.bool,
  onDataChange: PropTypes.func.isRequired,
  noLabel: PropTypes.bool,
};

ArrayComponent.defaultProps = {
  data: [],
  dataChanges: undefined,
  idSuffix: '',
  isCollapsed: false,
  pathToData: '',
  readOnly: false,
  textOnly: false,
  expanded: false,
  noLabel: false,
};

export default injectIntl(ArrayComponent);
